/**
 * Versioned, self-contained project format.
 *
 * Stored in IndexedDB and exported as `.stitchcraft.json`. The original image is embedded
 * (base64 in files, Blob in IndexedDB) — never a blob: URL, which would not survive a reload.
 * Generated stitches are not stored; they are regenerated from the design.
 *
 * To change the format: bump PROJECT_VERSION and add a migration from the previous version.
 */
import { t } from '../i18n/i18n';
import { machineById } from '../engine/model/machines';
import { defaultFill, defaultMachine, defaultRunning, defaultSatin } from '../engine/model/params';
import type { Design, EmbObject, StitchParams } from '../engine/model/types';

export const PROJECT_FORMAT = 'stitchcraft-project';
export const PROJECT_VERSION = 1;
export const PROJECT_EXTENSION = '.stitchcraft.json';
export const MAX_PROJECT_FILE_BYTES = 60 * 1024 * 1024;

export interface StoredImage {
  mime: string;
  /** Base64 data (file format) — in IndexedDB the Blob is stored instead. */
  base64: string;
  name: string;
  width: number;
  height: number;
}

export interface ProjectFileV1 {
  format: typeof PROJECT_FORMAT;
  version: 1;
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  app: { name: 'StitchCraft'; version: string };
  design: Design;
  image: StoredImage | null;
  /** Folder in the project list (added later; missing = no folder). */
  folder?: string | null;
}

export type ProjectFile = ProjectFileV1;

export const MAX_FOLDER_LENGTH = 100;

/** Trimmed folder name, or null for none. */
export function normalizeFolder(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.replace(/\s+/g, ' ').trim().slice(0, MAX_FOLDER_LENGTH);
  return s || null;
}

export class ProjectFormatError extends Error {}

type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/** migrations[n] upgrades a version-n project to version n+1. */
const migrations: Record<number, Migration> = {};

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Upgrades a parsed project of any supported version to the current version and validates it. */
export function migrateProject(raw: unknown): ProjectFile {
  if (!isObj(raw) || raw.format !== PROJECT_FORMAT) throw new ProjectFormatError(t('The file is not a StitchCraft project.'));
  let v = raw.version;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) throw new ProjectFormatError(t('The project file has no valid version number.'));
  if (v > PROJECT_VERSION) {
    throw new ProjectFormatError(t('The project was made with a newer version (format {v}). Update StitchCraft to open it.', { v }));
  }
  let cur: Record<string, unknown> = raw;
  while (v < PROJECT_VERSION) {
    const m = migrations[v];
    if (!m) throw new ProjectFormatError(t('Missing migration from version {v}.', { v }));
    cur = m(cur);
    v++;
    cur.version = v;
  }
  return validateProject(cur);
}

function fail(msg: string): never {
  throw new ProjectFormatError(t('Invalid project: {msg}', { msg }));
}

const num = (v: unknown, what: string): number => (typeof v === 'number' && Number.isFinite(v) ? v : fail(t('{what} must be a number', { what })));

function normalizeParams(p: unknown): StitchParams {
  if (!isObj(p)) fail(t('stitch parameters are missing'));
  const base = p.type === 'fill' ? defaultFill() : p.type === 'satin' ? defaultSatin() : p.type === 'running' ? defaultRunning() : fail(t('unknown stitch type {type}', { type: String(p.type) }));
  // fill in fields added later with defaults, keep stored values
  const merged = { ...base, ...p } as Record<string, unknown>;
  if ('underlay' in base) merged.underlay = { ...(base as { underlay: object }).underlay, ...(isObj(p.underlay) ? p.underlay : {}) };
  return merged as unknown as StitchParams;
}

function validateObject(o: unknown, i: number): EmbObject {
  if (!isObj(o)) fail(t('object {i} is invalid', { i }));
  if (typeof o.id !== 'string' || typeof o.colorId !== 'string') fail(t('object {i} has no id/colour', { i }));
  const shape = o.shape;
  if (!isObj(shape) || !['area', 'line', 'column'].includes(String(shape.kind))) fail(t('object {i} has an invalid shape', { i }));
  const tf = isObj(o.transform) ? o.transform : fail(t('object {i} has no transform', { i }));
  return {
    id: o.id,
    name: typeof o.name === 'string' ? o.name : t('Object {n}', { n: i + 1 }),
    colorId: o.colorId,
    visible: o.visible !== false,
    locked: o.locked === true,
    shape: shape as unknown as EmbObject['shape'],
    transform: { x: num(tf.x, 'x'), y: num(tf.y, 'y'), rotation: num(tf.rotation, t('rotation')), scaleX: num(tf.scaleX, t('scale')), scaleY: num(tf.scaleY, t('scale')) },
    params: normalizeParams(o.params),
    entry: isObj(o.entry) ? { x: num(o.entry.x, 'inngang'), y: num(o.entry.y, 'inngang') } : null,
    exit: isObj(o.exit) ? { x: num(o.exit.x, 'utgang'), y: num(o.exit.y, 'utgang') } : null,
    tieIn: o.tieIn !== false,
    tieOff: o.tieOff !== false,
    ...(typeof o.patch === 'string' && ['background', 'border', 'placement', 'tackdown'].includes(o.patch) ? { patch: o.patch as EmbObject['patch'] } : {}),
  };
}

function validateProject(p: Record<string, unknown>): ProjectFile {
  const d = isObj(p.design) ? p.design : fail(t('design is missing'));
  if (!Array.isArray(d.objects) || !Array.isArray(d.palette)) fail(t('design is missing objects or palette'));
  const machine = { ...defaultMachine(), ...(isObj(d.machine) ? d.machine : {}) } as Design['machine'];
  if (!machineById(machine.machineId)) machine.machineId = null; // older projects, or a machine no longer in the catalogue
  const design: Design = {
    name: typeof d.name === 'string' ? d.name : 'Uten navn',
    widthMm: num(d.widthMm, t('width')),
    heightMm: num(d.heightMm, t('height')),
    palette: (d.palette as unknown[]).map((c, i) => {
      if (!isObj(c) || typeof c.id !== 'string' || typeof c.hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(c.hex)) fail(t('colour {i} is invalid', { i }));
      return { id: c.id, name: typeof c.name === 'string' ? c.name : c.hex, hex: c.hex.toLowerCase(), ...(typeof c.brand === 'string' ? { brand: c.brand } : {}), ...(typeof c.code === 'string' ? { code: c.code } : {}) };
    }),
    objects: (d.objects as unknown[]).map(validateObject),
    machine,
    backdrop: isObj(d.backdrop) ? (d.backdrop as unknown as Design['backdrop']) : null,
  };
  const img = p.image;
  let image: StoredImage | null = null;
  if (isObj(img)) {
    if (typeof img.base64 !== 'string' || typeof img.mime !== 'string') fail(t('image data is invalid'));
    if (!/^image\/(png|jpeg|svg\+xml)$/.test(img.mime)) fail(t('unknown image type {mime}', { mime: img.mime }));
    image = { mime: img.mime, base64: img.base64, name: String(img.name ?? 'bilde'), width: Number(img.width) || 0, height: Number(img.height) || 0 };
  }
  return {
    format: PROJECT_FORMAT,
    version: 1,
    id: typeof p.id === 'string' ? p.id : fail(t('id is missing')),
    name: typeof p.name === 'string' ? p.name : design.name,
    createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date().toISOString(),
    updatedAt: typeof p.updatedAt === 'string' ? p.updatedAt : new Date().toISOString(),
    app: { name: 'StitchCraft', version: typeof (p.app as Record<string, unknown> | undefined)?.version === 'string' ? String((p.app as Record<string, unknown>).version) : '0.1.0' },
    design,
    image,
    folder: normalizeFolder(p.folder),
  };
}

export function serializeProject(p: ProjectFile): string {
  return JSON.stringify(p);
}

export function parseProject(text: string): ProjectFile {
  if (text.length > MAX_PROJECT_FILE_BYTES) throw new ProjectFormatError(t('The project file is too large.'));
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new ProjectFormatError(t('The project file is not valid JSON.'));
  }
  return migrateProject(raw);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
